import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable, { ConditionalStyles } from "react-data-table-component";
import { FaTimes, FaCheck } from "react-icons/fa";
import { deleteDevicePinpad, editDevicePinpad, getAllDevicePinpad, saveDevicePinpad } from "../api/appliApi";
import { format } from "date-fns";
import fr from "date-fns/locale/fr";
import FilterComponent from "../components/FilterComponent";
import CustomLoader from "../components/LoaderComponent";
import { customHeader, paginationOptions } from "../utils/StyledDataTable";
import { Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { ConfirmModal } from "../components/ConfirmModal";
import { ToastContainer, toast } from "react-toastify";
import { TableContainer } from "../utils/StyledComponents";
import plusCircle from "../asset/img/icon/plus-circle.svg";
import { DeviceBasePinpadInterface } from "../utils/Type";

const clearFormData = () => ({
    id: "",
    type: "",
    manufacturer: "",
    model: "",
    active: false,
    priority: ""
});

const DevicePinpad = () => {
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [pending, setPending] = useState(true);
    const [datas, setDatas] = useState<DeviceBasePinpadInterface[]>([]);
    const [modalShow, setModalShow] = React.useState(false);
    const [confirmShow, setConfirmShow] = React.useState(false);
    const [selectedName, setSelectedName] = useState('');
    const [idToDelete, setIdToDelete] = useState('');
    const [formData, setFormData] = useState(clearFormData());
    const submitLockRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    const fetchData = async () => {
        try {
            const data = await getAllDevicePinpad();
            if (Array.isArray(data.data)) {
                setDatas(data.data);
            } else {
                setDatas([]);
            }
        } catch (error) {
            setDatas([]);
        } finally {
            setPending(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredData = useMemo(() => {
        const txt = filterText.toLowerCase();
        return datas.filter(d =>
            d.id.toString().includes(txt) ||
            d.type.toLowerCase().includes(txt) ||
            d.manufacturer.toLowerCase().includes(txt) ||
            d.model.toLowerCase().includes(txt)
        );
    }, [filterText, datas]);

    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterText(event.target.value);
    };

    const columns = [
        {
            name: 'Id',
            selector: (row: { id: string }) => row.id,
            sortable: true,
            width: '7%'
        },
        {
            name: 'Model',
            selector: (row: { model: string }) => row.model,
            sortable: true,
            width: '30%'
        },
        {
            name: 'Type',
            selector: (row: { type: string }) => row.type,
            sortable: true,
        },
        {
            name: 'Manufacturer',
            selector: (row: { manufacturer: string }) => row.manufacturer,
            sortable: true,
        },
        {
            name: 'Favori',
            selector: (row: { priority: number }) => row.priority,
            sortable: true,
            width: '7%'
        },
        {
            name: 'Active',
            selector: (row: { active: boolean }) => row.active,
            sortable: true,
            width: '7%',
            cell: (row: { active: boolean }) => row.active ? <FaCheck /> : <FaTimes />
        },

        {
            name: 'Date de création',
            selector: (row: DeviceBasePinpadInterface) => format(new Date(row.createdAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
        },
        {
            name: 'Date de modification',
            selector: (row: DeviceBasePinpadInterface) => format(new Date(row.updatedAt), "dd/MM/yyyy HH:mm", { locale: fr }),
            sortable: true,
        },
        {
            name: 'Action',
            cell: (row: any) => (
                <>
                    <MdEdit size={24} onClick={() => handleEdit(row)} style={{ cursor: "pointer" }} />
                    <MdDeleteForever size={24} onClick={() => handleShowConfirm(row.id, row.model)} style={{ cursor: "pointer" }} />
                </>
            ),
            ignoreRowClick: true
        }
    ];

    const conditionalRowStyles: ConditionalStyles<DeviceBasePinpadInterface>[] = [
        {
            when: row => filteredData.indexOf(row) % 2 !== 0,
            style: { backgroundColor: "#F6F8FD" },
        },
    ];

    const handleClear = () => {
        if (filterText) {
            setResetPaginationToggle(!resetPaginationToggle);
            setFilterText('');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        })
    }

    const notify = (message: string) => toast(message);
    const notifyError = (message: string) => toast.error(message);

    const handleClearForm = () => { setFormData(clearFormData()); setSelectedName(''); };

    const handleValidate = async () => {
        if (submitLockRef.current || isSaving) return;

        submitLockRef.current = true;
        setIsSaving(true);

        try {
            if (!formData.model.trim()) {
                notifyError("Le modele est requis.");
                return;
            }
            if (formData.id) {
                await editDevicePinpad.put(formData);
            } else {
                await saveDevicePinpad.post(formData);
            }
            fetchData();
            handleClearForm();
            notify('Enregistrer avec success !');
            setModalShow(false);
        } catch (error) {
            notifyError("Erreur lors de l'enregistrement.")
        } finally {
            setIsSaving(false);
            submitLockRef.current = false;
        }
    }

    const handleEdit = (item: any) => {
        setFormData({
            id: item.id,
            type: item.type,
            manufacturer: item.manufacturer,
            model: item.model,
            active: item.active,
            priority: item.priority
        });
        setSelectedName(item.model);
        setModalShow(true);
    }

    const handleDelete = async () => {
        try {
            await deleteDevicePinpad(idToDelete);
            fetchData();
            setSelectedName('');
            setIdToDelete('');
            notify('Supprimer avec success !');
            setConfirmShow(false);
        } catch (error) {
            notifyError("Erreur lors de la suppression.")
        }
    }

    const handleShowConfirm = (id: string, name: string) => {
        setIdToDelete(id);
        setSelectedName(name);
        setConfirmShow(true);
    }
    const handleHideConfirm = () => {
        setSelectedName('');
        setIdToDelete('');
        setConfirmShow(false);
    }

    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">Liste des pinpads</span>
                <button className="btn-add-circle" onClick={() => setModalShow(true)}>
                    <span className="btn-text">Ajouter un pinpad</span>
                    <span className="plus-icon-circle"><img src={plusCircle} alt="icone +" /></span>
                </button>
            </div>

            <ToastContainer />

            <FilterComponent
                filterText={filterText}
                onFilter={handleFilter}
                onClear={handleClear}
            />

            <TableContainer>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 150px)"
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    paginationComponentOptions={paginationOptions}
                    noDataComponent={<p style={{ marginTop: "20px" }}>Aucun enregistrement trouvé.</p>}
                />
            </TableContainer>

            <FormModal
                show={modalShow}
                onValidate={handleValidate}
                onAbort={() => {
                    if (isSaving) return;
                    setModalShow(false);
                    handleClearForm();
                }}
                title={`Détails du pinpad${selectedName ? ` "${selectedName}"` : ''}`}
                isBusy={isSaving}
                btnValidate={isSaving ? "Enregistrement..." : "Enregistrer"}
                abortDisabled={isSaving}
                body={
                    <>
                        <Form>
                            <Form.Group className="mb-3" controlId="devicePinpad.ControlInput">
                                <Form.Label>Nom du model</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Model"
                                    name="model"
                                    value={formData.model}
                                    onChange={handleChange}
                                    autoFocus
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" >
                                <Form.Label>Manufacturer</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Manufacturer"
                                    name="manufacturer"
                                    value={formData.manufacturer}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" >
                                <Form.Label>Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Type"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" >
                                <Form.Label>Priorité d’affichage dans la liste (optionnel)</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Priorité"
                                    name="priority"
                                    value={formData.priority}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            <div className="d-flex flex-column align-items-start gap-2">
                                <Form.Group className="d-flex align-items-start gap-4">
                                    <Form.Label className="mb-0">Active</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="switch-active"
                                        name="active"
                                        checked={formData.active}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                        </Form>
                    </>
                }
            />

            <ConfirmModal
                show={confirmShow}
                onValidate={handleDelete}
                onAbort={handleHideConfirm}
                title="Supprimer le pinpad"
                body={"Êtes-vous sûr de vouloir supprimer le pinpad, \"" + selectedName + "\" ?"}
            />
        </div>

    );
};

export default DevicePinpad;